import { useState, useEffect } from "react";
import { useToast } from "./Toast";
import { apiUrl } from "../config/api";

interface Webhook {
  id: string;
  ownerEmail: string;
  url: string;
  events: ("scan.completed" | "scan.failed" | "scan.deleted")[];
  secret: string;
  active: boolean;
  createdAt: string;
  lastTriggeredAt?: string;
  failCount: number;
}

export function WebhookManager() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showSecret, setShowSecret] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    url: "",
    events: { "scan.completed": false, "scan.failed": false, "scan.deleted": false }
  });

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(apiUrl("/webhooks"), { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setWebhooks(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      setError("Failed to load webhooks");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createWebhook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.url) {
      toast('Webhook URL is required', 'warning');
      return;
    }

    const selectedEvents = Object.entries(formData.events)
      .filter(([, selected]) => selected)
      .map(([event]) => event as "scan.completed" | "scan.failed" | "scan.deleted");

    if (selectedEvents.length === 0) {
      toast("Select at least one event", "warning");
        setError("Select at least one event");
      return;
    }

    try {
      const res = await fetch(apiUrl("/webhooks"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          url: formData.url,
          events: selectedEvents
        })
      });

      if (res.ok) {
        const newWebhook = await res.json();
        setWebhooks([...webhooks, newWebhook]);
        setFormData({ url: "", events: { "scan.completed": false, "scan.failed": false, "scan.deleted": false } });
        setShowForm(false);
      } else {
        setError("Failed to create webhook");
      }
    } catch (err) {
      setError("Failed to create webhook");
      console.error(err);
    }
  };

  const deleteWebhook = async (id: string) => {
    try {
      const res = await fetch(apiUrl(`/webhooks/${id}`), {
        method: "DELETE",
        credentials: "include"
      });

      if (res.ok) {
        setWebhooks(webhooks.filter(w => w.id !== id));
      }
    } catch (err) {
      setError("Failed to delete webhook");
      console.error(err);
    }
  };

  const testWebhook = async (id: string) => {
    try {
      const res = await fetch(apiUrl(`/webhooks/${id}/test`), {
        method: "POST",
        credentials: "include"
      });

      if (res.ok) {
        const result = await res.json();
        alert(`Test sent! Status: ${result.statusCode}`);
      } else {
        setError("Failed to test webhook");
      }
    } catch (err) {
      setError("Failed to test webhook");
      console.error(err);
    }
  };

  const containerStyle: React.CSSProperties = {
    padding: "20px",
    backgroundColor: "var(--bg-primary)",
    color: "var(--text-primary)",
    maxWidth: "1200px",
    margin: "0 auto"
  };

  const cardStyle: React.CSSProperties = {
    padding: "15px",
    backgroundColor: "var(--nav-inactive-bg)",
    borderRadius: "8px",
    marginBottom: "15px",
    border: "1px solid var(--border-color)"
  };

  const buttonStyle: React.CSSProperties = {
    padding: "10px 15px",
    backgroundColor: "var(--primary-color)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    marginRight: "10px"
  };

  const formStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "15px",
    backgroundColor: "var(--nav-inactive-bg)",
    borderRadius: "8px",
    marginBottom: "15px"
  };

  const inputStyle: React.CSSProperties = {
    padding: "8px 12px",
    borderRadius: "4px",
    border: "1px solid var(--border-color)",
    backgroundColor: "var(--bg-primary)",
    color: "var(--text-primary)"
  };

  if (loading) {
    return <div style={containerStyle}>Loading webhooks...</div>;
  }

  return (
    <div style={containerStyle}>
      <h1 style={{ fontSize: "24px", marginBottom: "20px" }}>Webhooks</h1>

      <button
        onClick={() => setShowForm(!showForm)}
        style={buttonStyle}
      >
        {showForm ? "Cancel" : "Add Webhook"}
      </button>

      {showForm && (
        <form onSubmit={createWebhook} style={formStyle}>
          <label style={{ fontSize: "14px" }}>Webhook URL</label>
          <input
            type="url"
            placeholder="https://example.com/webhook"
            value={formData.url}
            onChange={e => setFormData({ ...formData, url: e.target.value })}
            style={inputStyle}
            required
          />

          <label style={{ fontSize: "14px" }}>Events</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {(Object.keys(formData.events) as Array<keyof typeof formData.events>).map(event => (
              <label key={event} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={formData.events[event]}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      events: { ...formData.events, [event]: e.target.checked }
                    })
                  }
                />
                {event}
              </label>
            ))}
          </div>

          <button type="submit" style={buttonStyle}>
            Create Webhook
          </button>
        </form>
      )}

      {error && (
        <div style={{
          padding: "10px 15px",
          backgroundColor: "#ff4444",
          borderRadius: "6px",
          marginBottom: "15px",
          color: "white"
        }}>
          {error}
        </div>
      )}

      <div>
        {webhooks.length === 0 ? (
          <p style={{ opacity: 0.7 }}>No webhooks configured</p>
        ) : (
          webhooks.map(webhook => (
            <div key={webhook.id} style={cardStyle}>
              <div style={{ marginBottom: "12px" }}>
                <div style={{ fontWeight: "500", marginBottom: "5px" }}>{webhook.url}</div>
                <div style={{ fontSize: "12px", opacity: 0.7 }}>
                  Events: {webhook.events.join(", ")}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.7 }}>
                  Status: {webhook.active ? "Active" : "Disabled"} | Failures: {webhook.failCount}
                </div>
                {webhook.lastTriggeredAt && (
                  <div style={{ fontSize: "12px", opacity: 0.7 }}>
                    Last triggered: {new Date(webhook.lastTriggeredAt).toLocaleString()}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: "10px" }}>
                <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={showSecret[webhook.id] || false}
                    onChange={e => setShowSecret({ ...showSecret, [webhook.id]: e.target.checked })}
                  />
                  Show Secret
                </label>
                {showSecret[webhook.id] && (
                  <div style={{
                    padding: "8px",
                    backgroundColor: "var(--bg-primary)",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                    fontSize: "11px",
                    wordBreak: "break-all",
                    marginTop: "5px"
                  }}>
                    {webhook.secret}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => testWebhook(webhook.id)}
                  style={{
                    ...buttonStyle,
                    backgroundColor: "var(--secondary-color)",
                    marginRight: "0"
                  }}
                >
                  Test
                </button>
                <button
                  onClick={() => deleteWebhook(webhook.id)}
                  style={{
                    ...buttonStyle,
                    backgroundColor: "var(--error-color)",
                    marginRight: "0"
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
